import useOffClick from '@flight-workspace/common-client/src/hooks/useOffClick';
import useTravelUser from '@flight-workspace/common-client/src/hooks/useTravelUser';
import {
  useKoreanUnitSeparators,
  withPriceSeperator,
} from '@flight-workspace/common-client/src/util/number';
import classNames from 'classnames/bind';
import { IPromotion } from 'common/promotion';
import { useToast } from 'common/searchBox/Toast';
import { IKeyStringValue } from 'common/types';
import {
  getClickCodeWithPrefix,
  getNpayLabelInfo,
  hasTravelClubFare,
} from 'common/utils';
import config from 'config/index';
import { TripType, useSearchParamsContext } from 'contexts/searchParams';
import useFlightsCards from 'hooks/useFlightsCards';
import PromotionInfo from 'list/common/promotionInfo';
import React, { useState } from 'react';
import getPromotions from '../promotion';
import { IFareDetail, IFareTypeList, IScheduleDetail } from '../types';
import { getPriceOfPerson, hasAdultNaverFare } from '../utils';
import styles from './index.module.scss';
import { Price } from './price';

const cx = classNames.bind(styles);

interface IProps {
  fare: IFareTypeList;
  fareKeys: string[];
  fareTypes: IKeyStringValue;
  foldFares: boolean;
  isTopPopularFlight?: boolean;
  nClickKey: string;
  promotions: IPromotion[];
  scheduleIndex: number;
  schedules: IScheduleDetail[][];
  selectedFareType?: string;
  tripType: TripType;
  selectFlight: (fareType: string) => void;
  toggleFoldFares: (value: React.SetStateAction<boolean>) => void;
}

const PriceTitles: { [key: string]: string } = {
  [TripType.RT]: '왕복 ',
  [TripType.OW]: '편도 ',
  [TripType.MD]: '전체 ',
  [TripType.NULL]: '',
};

const ItemPriceList = ({
  fare,
  fareKeys,
  fareTypes,
  foldFares,
  isTopPopularFlight = false,
  nClickKey,
  promotions,
  scheduleIndex,
  schedules,
  tripType,
  selectFlight,
  toggleFoldFares,
}: IProps) => {
  const ref = useOffClick(
    () => {
      toggleFoldFares(true);
    },
    { stopPropagation: true },
  );
  const {
    searchParams: { stayLength },
  } = useSearchParamsContext();
  const [showAll, toggleShowAll] = useState(false);
  const { isLogin, login } = useTravelUser({
    nAuthWithUrlParam: config.nAuthWithUrlParam,
  });
  // 도착일 미정인 경우 편도 UI에서 왕복요금을 노출
  const priceTitle = stayLength ? '왕복' : PriceTitles[tripType];
  const scheduleNumber = scheduleIndex + 1;
  const withPrefix = getClickCodeWithPrefix(nClickKey);
  // 요금타입(성인, 성인/신한카드)별 요금리스트
  const lowestFare: IFareDetail[] = fare[fareKeys[0]];
  // 최저가 요금 타입의 상세 요금 데이터
  // e.g. 성인(A01)의 최저가 요금 상세
  const lowestFareDetail: IFareDetail = lowestFare[0];

  const [promotion] = getPromotions(promotions, schedules, lowestFareDetail);

  // 최저가 요금에 대한 즉시 프로모션 진행 여부 체크
  const isDiscountPromotion = hasAdultNaverFare(lowestFareDetail);
  const isNPayPromotion = isDiscountPromotion && !!promotion;

  // 최저가 요금에 대한 멤버쉽 할인 요금 노출 여부 체크
  // 비로그인은 로그인 유도
  // 로그인은 조건에 맞으면 트래블클럽 로고 노출
  //   노출 조건:
  //     1. 로그인
  //     2. 멤버쉽 요금이 Top5 이내
  const isTravelClubInLowest = hasTravelClubFare(lowestFare);

  const flightCards = useFlightsCards();
  const [showNpayLabelPopup, setShowNpayLabelPopup] = useToast();

  // 기본
  // 트래블클럽(할인요금) 존재시
  // - 공통: 트래블클럽 로고 노출
  // - 비로그인: 로그인 버튼 노출
  // - 로그인: 최저가 노출
  const defaultSummary = (
    fareData: IFareDetail,
    hasPayPointPromotion: boolean,
  ) => (
    <div className={cx('defaultSummary')} itemScope>
      <span className={cx('type')} itemProp="name">
        {fareTypes[fareKeys[0]]}
      </span>
      {isTravelClubInLowest && (
        <span className={cx('condition')} itemProp="itemCondition">
          <span className={cx('travelclub')}>
            <i className={cx('service')} title="트래블클럽" />{' '}
            <span className={cx('txt')}>할인</span>
          </span>
        </span>
      )}
      {isTravelClubInLowest && !isLogin && !hasPayPointPromotion && (
        <span className={cx('login')} itemProp="offers">
          <button
            type="button"
            className={cx('txt')}
            onClick={() => login(window.location.href)}
          >
            특가확인
          </button>
          <i className={cx('nlogin')} title="NAVER 로그인" />
        </span>
      )}
      <b className={cx('usual')} itemProp="offers">
        {priceTitle}
        <i className={cx('num')}>
          {withPriceSeperator(getPriceOfPerson(fareData.Adult) || 0)}
        </i>
        <span className={cx('unit')}>원~</span>
      </b>
    </div>
  );

  // 즉시 할인 프로모션
  // 트래블클럽(할인요금) 존재시
  // 로그인/비로그인 UI 동일
  const directPromotionSummary = (fareData: IFareDetail) => {
    const originalFare = getPriceOfPerson(fareData.Adult);
    const discountFare = getPriceOfPerson(fareData.Adult, true);
    return (
      <div className={cx('directPromotionSummary')} itemScope>
        {isTravelClubInLowest && (
          <span className={cx('condition')} itemProp="itemCondition">
            <span className={cx('travelclub')}>
              <i className={cx('service')} title="트래블클럽" />{' '}
              <span className={cx('txt')}>할인</span>
            </span>
          </span>
        )}
        <span className={cx('type')} itemProp="name">
          {fareTypes[fareKeys[0]]}
        </span>
        <del className={cx('before')} itemProp="offers">
          <i className={cx('num')}>{withPriceSeperator(originalFare)}</i>
          <span className={cx('unit')}>원~</span>
        </del>
        <span className={cx('condition')} itemProp="itemCondition">
          <span className={cx('directpromotion')}>
            {useKoreanUnitSeparators(originalFare - discountFare)}원 할인
          </span>
        </span>
        <b className={cx('promoted')} itemProp="offers">
          {priceTitle}
          <i className={cx('num')}>{withPriceSeperator(discountFare)}</i>
          <span className={cx('unit')}>원~</span>
        </b>
      </div>
    );
  };

  const firstItemnPayLabelInfo = getNpayLabelInfo(
    lowestFareDetail.FareType,
    flightCards,
  );

  // 최저가 요금 타입
  const firstItem = (
    // 즉시 할인 프로모션과 자체 적립 프로모션은 동시에 진행되지 않음
    <div className={cx('firstItem')}>
      {isDiscountPromotion
        ? directPromotionSummary(lowestFareDetail)
        : defaultSummary(lowestFareDetail, isNPayPromotion)}
      {isNPayPromotion && (
        <div className={cx('firstItem_promotion')}>
          <span className={cx('desc')}>
            <PromotionInfo
              npayIcon={<i className={cx('npaypoint')} title="npaypoint" />}
              paymentMethod={
                promotion.paymentMethod && fareTypes[fareKeys[0]].split('/')[1]
              }
              summaryByType={promotion.summaryByType}
            />
          </span>
          <b className={cx('price')}>
            <i className={cx('num')}>
              {withPriceSeperator(promotion.discountedPrice)}
            </i>
            <span className={cx('unit')}>원~</span>
          </b>
        </div>
      )}
      {!!firstItemnPayLabelInfo && (
        <div className={cx('firstItem_npay_percent')} itemProp="itemCondition">
          <button
            className={cx('label_npay')}
            onClick={() =>
              setShowNpayLabelPopup(showNpayLabelPopup ? '' : 'show')
            }
            onMouseEnter={() =>
              setShowNpayLabelPopup(showNpayLabelPopup ? '' : 'show')
            }
            aria-haspopup="true"
            aria-expanded={!!showNpayLabelPopup}
          >
            <i className={cx('label')} aria-label="네이버페이" />
            {`${firstItemnPayLabelInfo.nPayPromotionAmount}% 적립`}
          </button>
          <div className={cx('popup')}>
            {firstItemnPayLabelInfo.nPayLabelPopupText}
          </div>
        </div>
      )}
    </div>
  );

  if (fareKeys.length === 1) {
    return (
      <div className={cx('ItemPriceList')}>
        <div className={cx('summary', 'is_only_item')}>{firstItem}</div>
      </div>
    );
  }

  if (foldFares) {
    return (
      <div className={cx('ItemPriceList')}>
        <div className={cx('summary')}>{firstItem}</div>
      </div>
    );
  }

  return (
    <div className={cx('ItemPriceList')} aria-expanded="true">
      <div className={cx('summary')}>{firstItem}</div>
      <div className={cx('detail')} ref={ref}>
        {(showAll ? fareKeys : fareKeys.slice(0, 10)).map((f, i) => {
          return (
            <Price
              fares={fare[f]}
              fareType={f}
              fareTypeName={fareTypes[f]}
              isTopPopularFlight={isTopPopularFlight}
              nClickKey={nClickKey}
              priceTitle={priceTitle}
              promotions={promotions}
              scheduleNumber={scheduleNumber}
              schedules={schedules}
              showNpayLabelPopup={showNpayLabelPopup}
              selectFlight={selectFlight}
              setShowNpayLabelPopup={setShowNpayLabelPopup}
            />
          );
        })}
        <div className={cx('btns')}>
          {!showAll && fareKeys.length > 10 && (
            <button
              type="button"
              className={cx('more')}
              data-event-nlog="click"
              data-event-area-code={withPrefix(
                `${isTopPopularFlight ? 'top' : ''}morefare${scheduleNumber}`,
              )}
              onClick={() => {
                toggleShowAll(true);
              }}
            >
              <i className={cx('count')}>
                <b className={cx('value')}>{fareKeys.length - 10}</b>
                <span className={cx('unit')}>개</span>
              </i>
              요금조건 더보기
            </button>
          )}
          <button
            type="button"
            className={cx('fold')}
            data-event-nlog="click"
            data-event-area-code={withPrefix(
              `${isTopPopularFlight ? 'top' : ''}fareclose${scheduleNumber}`,
            )}
            onClick={() => {
              toggleFoldFares(true);
              toggleShowAll(false);
            }}
          >
            접기
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemPriceList;
